import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Card, CardContent, Button,
  CircularProgress, Alert, Table, TableBody, TableRow, TableCell, Chip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const FUNCTIONS_BASE = 'https://us-central1-torch-beach-app.cloudfunctions.net';

function fmtAmount(cents) { return (cents / 100).toFixed(2); }

export default function TournamentCancel() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading');
  const [reg, setReg] = useState(null);
  const [refundMsg, setRefundMsg] = useState('');
  const [refundOk, setRefundOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${FUNCTIONS_BASE}/cancelLookup?token=${encodeURIComponent(token)}`);
        if (res.status === 404) { setState('invalid'); return; }
        const data = await res.json();
        if (!data.reg) { setState('invalid'); return; }
        setReg(data.reg);
        setState(data.reg.status === 'cancelled' ? 'already' : 'confirm');
      } catch {
        setState('invalid');
      }
    }
    if (token) load(); else setState('invalid');
  }, [token]);

  async function handleConfirm(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/cancelRegistration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      setRefundMsg(data.refund_msg || '');
      setRefundOk(data.refund_ok ?? false);
      setState('done');
    } catch {
      setRefundMsg('An error occurred. Please try again or contact the organizer.');
      setRefundOk(false);
      setState('done');
    } finally {
      setSubmitting(false);
    }
  }

  if (state === 'loading') return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  if (state === 'invalid') return (
    <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h2" gutterBottom>❌</Typography>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Link Not Found</Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>This cancellation link is invalid or has expired.</Typography>
      <Button variant="contained" startIcon={<HomeIcon />} onClick={() => navigate('/tournaments')}>Back to Tournaments</Button>
    </Container>
  );

  if (state === 'already') return (
    <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h2" gutterBottom>ℹ️</Typography>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Already Cancelled</Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        This registration for <strong>{reg.tournament_name}</strong> ({reg.division} Division) has already been cancelled.
      </Typography>
      <Button variant="contained" startIcon={<HomeIcon />} onClick={() => navigate('/tournaments')}>Back to Tournaments</Button>
    </Container>
  );

  if (state === 'done') return (
    <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h2" gutterBottom>✅</Typography>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Registration Cancelled</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Your registration for <strong>{reg.tournament_name}</strong> ({reg.division} Division) has been cancelled.
      </Typography>
      {refundMsg && (
        <Alert severity={refundOk ? 'success' : 'warning'} icon={refundOk ? <CheckCircleIcon /> : undefined} sx={{ mb: 3, textAlign: 'left' }}>
          {refundMsg}
        </Alert>
      )}
      <Button variant="contained" startIcon={<HomeIcon />} onClick={() => navigate('/tournaments')}>Back to Tournaments</Button>
    </Container>
  );

  // Confirm state
  const hasPaid = reg && (reg.payment_status === 'paid' || reg.payment_status === 'half_paid');
  const partnerName = reg?.partner_name ||
    (reg?.partner_first_name ? `${reg.partner_first_name} ${reg.partner_last_name || ''}`.trim() : null);

  const paymentCell = {
    paid:      <Chip label={`Paid – $${fmtAmount(reg.amount_paid)}`} color="success" size="small" />,
    half_paid: <Chip label={`50% Deposit – $${fmtAmount(reg.amount_paid)}`} color="warning" size="small" />,
  }[reg.payment_status] || <Chip label="No payment on file" size="small" />;

  return (
    <Container maxWidth="sm" sx={{ py: 5 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h2" gutterBottom>⚠️</Typography>
        <Typography variant="h4" fontWeight="bold">Cancel Registration?</Typography>
        <Typography color="text.secondary">Please review the details below before confirming.</Typography>
      </Box>

      <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Table size="small">
            <TableBody>
              {[
                ['Tournament', reg.tournament_name],
                ['Division', reg.division],
                ['Player', `${reg.first_name} ${reg.last_name}`],
                ...(partnerName ? [['Partner', partnerName]] : []),
                ['Payment', paymentCell],
              ].map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold', border: 0, width: '40%' }}>{label}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: 0 }}>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {hasPaid && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Refund:</strong> Your payment of <strong>${fmtAmount(reg.amount_paid)}</strong> will be
          refunded to your original payment method. Please allow 5–10 business days.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="contained" color="error" size="large" fullWidth
          startIcon={submitting ? null : <CancelIcon />}
          disabled={submitting} onClick={handleConfirm}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : 'Yes, Cancel My Registration'}
        </Button>
        <Button variant="outlined" size="large" fullWidth startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/tournaments')}>
          Never Mind, Keep My Spot
        </Button>
      </Box>
    </Container>
  );
}
